import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config';

const pagingAndSortingSchema = z.object({
	paging: z.object({
		pageSize: z.number(),
		pageNumber: z.number(),
	}),
	sorting: z.object({
		columnName: z.string(),
		sortOrder: z.union([z.literal(1), z.literal(2)]),
	}),
});

export const requestSchema = z
	.object({
		transactionDateFrom: z.string().optional(),
		transactionDateTo: z.string().optional(),
		shoryTransactionRefNo: z.string().optional(),
		paymentGatewayRefNo: z.string().optional(),
		paymentGatewayId: z.number().optional(),
		acquirerID: z.string().optional(),
		policyNumber: z.string().optional(),
		cardTypeId: z.number().optional(),
		paymentMethodId: z.number().optional(),
		paymentPricingTypeId: z.number().optional(),
		transactionTypeIds: z.array(z.number()).optional(),
		policyIssueDateStart: z.string().optional(),
		policyIssueDateEnd: z.string().optional(),
		customerInvoiceNumber: z.string().optional(),
		actualPaidAmountInclVATFrom: z.number().optional(),
		actualPaidAmountInclVATTo: z.number().optional(),
		refundedAmountInclVATFrom: z.number().optional(),
		refundedAmountInclVATTo: z.number().optional(),
		isPaid: z.boolean().optional(),
		paymentIds: z.array(z.string()).optional(),
	})
	.merge(pagingAndSortingSchema);

const responseRecordSchema = z.object({
	transactionDate: z.string(),
	gatewayName: z.string(),
	cardType: z.string(),
	paymentMethod: z.string(),
	shoryTransactionNo: z.string(),
	paymentGatewayRefNo: z.string(),
	acquirerID: z.string(),
	actualPaidAmountExclVAT: z.number(),
	vatPercentageOnActualPaidAmount: z.number(),
	vatAmountOnActualPaidAmount: z.number(),
	actualPaidAmountInclVAT: z.number(),
	paymentPricingType: z.string(),
	cardFeesPercentage: z.number(),
	totalFeesAmountExclVAT: z.number(),
	vatPercentageOnTotalFeesAmount: z.number(),
	vatAmountOnTotalFeesAmount: z.number(),
	totalFeesAmountInclVAT: z.number(),
	cardFeesAmountExclVAT: z.number(),
	vatPercentageOnCardFeesAmount: z.number(),
	vatAmountOnCardFeesAmount: z.number(),
	cardFeesAmountInclVAT: z.number(),
	authorizationFeesExclVAT: z.number(),
	vatPercentageOnAuthorizationFees: z.number(),
	vatAmountOnAuthorizationFees: z.number(),
	authorizationFeesInclVAT: z.number(),
	refundFeesExclVAT: z.number(),
	vatPercentageOnRefundFees: z.number(),
	vatAmountOnRefundFees: z.number(),
	refundFeesInclVAT: z.number(),
	voidFeesExclVAT: z.number(),
	vatPercentageOnVoidFees: z.number(),
	vatAmountOnVoidFees: z.number(),
	voidFeesInclVAT: z.number(),
	transactionType: z.string(),
	invoiceNum: z.string(),
	refundedAmountExclVAT: z.number(),
	vatPercentageOnRefundedAmount: z.number(),
	vatAmountOnRefundedAmount: z.number(),
	refundedAmountInclVAT: z.number(),
	invoiceURL: z.string(),
	policyNumber: z.string(),
	encryptedPolicyId: z.string(),
	policyIssueDate: z.string(),
	isPaidOut: z.boolean(),
	payoutDate: z.string(),
	payoutReferenceNumber: z.string(),
	encryptedPaymentId: z.string(),
});

const responseSchema = z.object({
	data: z.array(responseRecordSchema),
	totalItemsCount: z.number(),
});
const postPaymentGatewaysReconciliationReport = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/admin/payment/payment-gateway-recon',
		method: 'POST',
		responseSchema,
		requestSchema,
		transformResponse: (response) => ({
			data: response.data.report,

			totalItemsCount: response.data.paging.totalRecords,
		}),
	});

export type PostPaymentGatewaysReconciliationReportRequest = z.infer<typeof requestSchema>;

export type PostPaymentGatewaysReconciliationReportResponse = TypeOfAPIResponse<
	ReturnType<typeof postPaymentGatewaysReconciliationReport>
>;

export type PostPaymentGatewaysReconciliationReportRecord = z.infer<typeof responseRecordSchema>;

export type PostPaymentGatewaysReconciliationReportError = TypeOfAPIError<
	ReturnType<typeof postPaymentGatewaysReconciliationReport>
>;

export default postPaymentGatewaysReconciliationReport;
